import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { InGameCurrency, Rarity } from '__generated__/globalTypes';
import { ButtonBase } from 'atoms/buttons/ButtonBase';
import { Energy } from 'atoms/icons/Energy';
import { Drawer } from 'atoms/layout/Drawer';
import { Horizontal, SBHorizontal, Vertical } from 'atoms/layout/flex';
import { HeadlineM, LabelL, LabelM, LabelS } from 'atoms/typography';
import { EnergyOnboardingInfoModal } from 'components/onboarding/EnergyOnboardingInfoModal';
import { useEnergyBalance } from 'hooks/useEnergyBalance';
import { formatScarcity } from 'lib/cards';
import { glossary } from 'lib/glossary';

const Wrapper = styled(Vertical)`
  background: var(--c-nd-50-opaque);
  padding: var(--triple-unit) var(--double-unit);
`;

const LearnMoreWrapper = styled(LabelM)`
  text-decoration: underline;
`;

const Detail = ({
  setOpenOnboarding,
}: {
  setOpenOnboarding: (open: boolean) => void;
}) => {
  const { energyBalance } = useEnergyBalance();
  const scarcities = {
    [InGameCurrency.LIMITED_ENERGY]: Rarity.limited,
    [InGameCurrency.RARE_ENERGY]: Rarity.rare,
    [InGameCurrency.SUPER_RARE_ENERGY]: Rarity.super_rare,
    [InGameCurrency.UNIQUE_ENERGY]: Rarity.unique,
  };

  return (
    <Vertical gap={1.5}>
      <LabelM bold color="var(--c-nd-600)">
        <FormattedMessage
          id="EnergyDetailDrawer.yourBalance"
          defaultMessage="Your balance"
        />
      </LabelM>
      {energyBalance.map(balance => {
        const scarcity =
          scarcities[balance.currency as keyof typeof scarcities];

        return (
          <SBHorizontal key={balance.currency}>
            <Horizontal gap={0.5}>
              <Energy size={1.5} variant={scarcity} />
              <LabelL bold>{formatScarcity(scarcity)}</LabelL>
            </Horizontal>
            <LabelS bold uppercase>
              {balance.amount}
            </LabelS>
          </SBHorizontal>
        );
      })}
      <ButtonBase onClick={() => setOpenOnboarding(true)}>
        <LearnMoreWrapper color="var(--c-nd-600)">
          <FormattedMessage
            id="EnergyDetailDrawer.learnMore"
            defaultMessage="Learn more about Energy"
          />
        </LearnMoreWrapper>
      </ButtonBase>
    </Vertical>
  );
};

type Props = {
  open: boolean;
  onClose: () => void;
};

export const EnergyDetailDrawer = ({ open, onClose }: Props) => {
  const [openOnboarding, setOpenOnboarding] = useState(false);

  return (
    <Drawer
      side="bottom"
      open={open}
      onBackdropClick={() => {
        setOpenOnboarding(false);
        onClose();
      }}
      fullScreen={false}
    >
      <Wrapper gap={2}>
        <Vertical gap={0.5}>
          <HeadlineM className="text-center">
            <FormattedMessage {...glossary.energy} />
          </HeadlineM>
          <LabelL className="text-center">
            <FormattedMessage
              id="EnergyBalanceHelp.description.mobile"
              defaultMessage="Applying Energy to Classic cards allows you to play them in in-season competitions"
            />
          </LabelL>
        </Vertical>
        {openOnboarding ? (
          <EnergyOnboardingInfoModal hideTitle />
        ) : (
          <Detail setOpenOnboarding={setOpenOnboarding} />
        )}
      </Wrapper>
    </Drawer>
  );
};
